import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { BarChart3, Lock, Sparkles, Activity, AlertTriangle, Lightbulb, PiggyBank, Wand2, FileCheck2, ArrowRight, ClipboardList } from "lucide-react";

function LockedGate({ addonName = "AI Financial Reports" }) {
  return (
    <div className="hoa-card royal-ring p-10 text-center relative overflow-hidden" data-testid="finrep-locked">
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-primary/25 blur-3xl" />
      <div className="relative">
        <Lock className="w-8 h-8 text-gold mx-auto mb-3" />
        <h2 className="text-2xl font-semibold mb-2">{addonName} is a premium add-on</h2>
        <p className="no-gold text-foreground/75 max-w-xl mx-auto mb-6">
          Monthly, quarterly, and annual AI reviews. A live financial health meter. Red flags, budget catches,
          reserve optimization ideas, and CPA-ready audit packets. 14-day free trial.
        </p>
        <a href="/subscription" className="inline-flex items-center gap-2 bg-gold text-secondary-foreground px-5 py-3 rounded-full font-semibold hover:brightness-110 glow-gold">
          <Sparkles className="w-4 h-4" /> Start free trial
        </a>
      </div>
    </div>
  );
}

const HEALTH_COLOR = { Critical: "text-destructive", "At Risk": "text-[hsl(32_87%_62%)]", Stable: "text-gold-soft", Healthy: "text-gold", Excellent: "text-[hsl(150_60%_60%)]" };

function HealthMeter({ score, label }) {
  const pct = Math.max(0, Math.min(100, score ?? 0));
  return (
    <div className="hoa-card p-6" data-testid="health-meter">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gold-soft">
          <Activity className="w-3.5 h-3.5" /> Financial health meter
        </div>
        <div className={`text-sm font-semibold ${HEALTH_COLOR[label] || "text-gold"}`}>{label}</div>
      </div>
      <div className="relative h-3 rounded-full overflow-hidden bg-muted">
        <div className="absolute inset-0 bg-gradient-to-r from-destructive via-[hsl(var(--gold))] to-[hsl(150_60%_55%)]" />
        <div className="absolute top-0 bottom-0" style={{ left: `${pct}%` }}>
          <div className="w-1 h-5 -mt-1 bg-foreground rounded-full" />
        </div>
      </div>
      <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
        <span>Critical</span>
        <span className="font-mono text-gold" data-testid="health-score">{pct}/100</span>
        <span>Excellent</span>
      </div>
    </div>
  );
}

export default function FinancialReports() {
  const [locked, setLocked] = useState(false);
  const [health, setHealth] = useState(null);
  const [reports, setReports] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ period_type: "monthly", period_label: new Date().toISOString().slice(0, 7) });
  const [generating, setGenerating] = useState(false);
  const [auditData, setAuditData] = useState(null);

  const load = async () => {
    try {
      const [h, l] = await Promise.all([api.get("/financial-reports/health"), api.get("/financial-reports")]);
      setHealth(h.data);
      setReports(l.data);
      setLocked(false);
    } catch (err) {
      if (err.response?.status === 402) setLocked(true);
    }
  };
  useEffect(() => { load(); }, []);

  const generate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      const { data } = await api.post("/financial-reports/generate", form);
      toast.success("AI report generated");
      setSelected(data);
      setOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not generate"); }
    finally { setGenerating(false); }
  };

  const loadAudit = async () => {
    try {
      const { data } = await api.get("/financial-reports/audit-prep");
      setAuditData(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not load"); }
  };

  if (locked) return (
    <div className="space-y-6" data-testid="financial-reports-page">
      <div>
        <div className="flex items-center gap-2 text-gold text-sm font-medium mb-2">
          <BarChart3 className="w-4 h-4" /> Financial Reports & Health
        </div>
        <h1 className="text-3xl lg:text-4xl font-semibold tracking-tight">Money stays with the community.</h1>
      </div>
      <LockedGate />
    </div>
  );

  const r = selected?.report;
  const snap = selected?.snapshot || health?.snapshot;

  return (
    <div className="space-y-6" data-testid="financial-reports-page">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-gold text-sm font-medium mb-2">
            <BarChart3 className="w-4 h-4" /> Financial Reports · Health · Reserves
          </div>
          <h1 className="text-3xl lg:text-4xl font-semibold tracking-tight">The money-stays-with-community view.</h1>
          <p className="no-gold text-foreground/75 mt-2 max-w-2xl">
            AI reviews your books monthly, quarterly, and annually. Health meter, red flags, budget catches, and creative
            reserve-building ideas — with CPA-ready packets when it's time to audit.
          </p>
        </div>
        <div className="flex gap-2">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full h-11 bg-gold text-secondary-foreground hover:brightness-110" data-testid="gen-report-btn">
                <Wand2 className="w-4 h-4 mr-2" /> Generate AI report
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Generate AI financial report</DialogTitle></DialogHeader>
              <form onSubmit={generate} className="space-y-4" data-testid="finrep-form">
                <div>
                  <Label>Period type</Label>
                  <Select value={form.period_type} onValueChange={(v) => setForm({ ...form, period_type: v })}>
                    <SelectTrigger className="mt-1.5 h-11" data-testid="rep-period-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="annual">Annual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Period label</Label>
                  <Input required value={form.period_label} onChange={(e) => setForm({ ...form, period_label: e.target.value })} className="mt-1.5 h-11" placeholder="2026-02 or Q1-2026 or 2025" data-testid="rep-period-label" />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={generating} className="rounded-full bg-gold text-secondary-foreground hover:brightness-110" data-testid="rep-submit">
                    {generating ? "Analyzing…" : "Generate"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          <Button variant="outline" className="rounded-full h-11" onClick={loadAudit} data-testid="audit-prep-btn">
            <FileCheck2 className="w-4 h-4 mr-2" /> CPA audit prep
          </Button>
        </div>
      </div>

      {health && <HealthMeter score={health.health_score} label={health.health_label} />}

      {health?.snapshot && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="finance-snapshot">
          {[
            ["Total charged", `$${health.snapshot.total_charged.toFixed(0)}`],
            ["Total paid", `$${health.snapshot.total_paid.toFixed(0)}`],
            ["Outstanding", `$${health.snapshot.outstanding.toFixed(0)}`],
            ["Delinquent owners", `${health.snapshot.delinquent_owner_count} / ${health.snapshot.owner_count}`],
          ].map(([l, v]) => (
            <div key={l} className="hoa-card p-4">
              <div className="text-xs text-muted-foreground uppercase">{l}</div>
              <div className="text-xl font-mono font-semibold text-gold mt-1">{v}</div>
            </div>
          ))}
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 space-y-3">
          <div className="text-xs uppercase tracking-widest text-gold-soft">Past reports</div>
          {reports.length === 0 && (
            <div className="hoa-card p-6 text-center text-muted-foreground text-sm" data-testid="reports-empty">No reports yet.</div>
          )}
          {reports.map((rep) => (
            <button key={rep.id} onClick={() => setSelected(rep)} className={`w-full text-left hoa-card p-4 ${selected?.id === rep.id ? "border-gold royal-ring" : "hover:bg-accent/30"}`} data-testid={`rep-item-${rep.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-gold-soft">{rep.period_type}</div>
                  <div className="no-gold text-sm font-semibold">{rep.period_label}</div>
                </div>
                <div className={`text-xs font-semibold ${HEALTH_COLOR[rep.report?.health_label] || "text-gold"}`}>
                  {rep.report?.health_score ?? "—"}
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-8 space-y-4">
          {!selected && !auditData && (
            <div className="hoa-card p-10 text-center text-muted-foreground" data-testid="no-selected">
              Generate a new report, open one from the list, or run CPA audit prep.
            </div>
          )}

          {selected && r && (
            <>
              <div className="hoa-card p-6" data-testid="report-summary">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-2xl font-semibold capitalize">{selected.period_type} · {selected.period_label}</h2>
                  <div className={`text-lg font-semibold ${HEALTH_COLOR[r.health_label] || "text-gold"}`}>{r.health_score}/100 · {r.health_label}</div>
                </div>
                <p className="no-gold text-foreground/90 leading-relaxed">{r.summary}</p>
              </div>

              {r.red_flags?.length > 0 && (
                <div className="hoa-card p-6" data-testid="red-flags">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gold-soft mb-3"><AlertTriangle className="w-3.5 h-3.5" /> Red flags</div>
                  <div className="space-y-2">
                    {r.red_flags.map((f, i) => (
                      <div key={`red-${f.issue}-${i}`} className="p-3 rounded-lg bg-accent/30 border border-border" data-testid={`red-${i}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase ${f.severity === "high" ? "bg-destructive/20 text-[hsl(355_80%_75%)]" : f.severity === "medium" ? "bg-gold text-secondary-foreground" : "bg-muted text-muted-foreground"}`}>{f.severity}</span>
                          <span className="no-gold text-sm font-semibold">{f.issue}</span>
                        </div>
                        {f.suggestion && <div className="no-gold text-xs text-foreground/75">→ {f.suggestion}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {r.budget_catches?.length > 0 && (
                <div className="hoa-card p-6" data-testid="budget-catches">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gold-soft mb-3"><ClipboardList className="w-3.5 h-3.5" /> Budget catches</div>
                  <ul className="space-y-1.5">
                    {r.budget_catches.map((c, i) => (
                      <li key={`bc-${i}-${String(c).slice(0, 24)}`} className="no-gold text-sm text-foreground/85">• {c}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                {r.ai_brainstorms?.length > 0 && (
                  <div className="hoa-card p-6" data-testid="brainstorms">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gold-soft mb-3"><Lightbulb className="w-3.5 h-3.5" /> AI brainstorms</div>
                    <ul className="space-y-1.5">
                      {r.ai_brainstorms.map((c, i) => (
                        <li key={`br-${i}-${String(c).slice(0, 24)}`} className="no-gold text-sm text-foreground/85">💡 {c}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {r.reserve_notes?.length > 0 && (
                  <div className="hoa-card p-6" data-testid="reserve-notes">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gold-soft mb-3"><PiggyBank className="w-3.5 h-3.5" /> Reserve notes</div>
                    <ul className="space-y-1.5">
                      {r.reserve_notes.map((c, i) => (
                        <li key={`rn-${i}-${String(c).slice(0, 24)}`} className="no-gold text-sm text-foreground/85">• {c}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {r.reserve_projects?.length > 0 && (
                <div className="hoa-card p-6" data-testid="reserve-projects">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gold-soft mb-3"><Sparkles className="w-3.5 h-3.5" /> Reserve-building projects (pulled from trends & similar communities)</div>
                  <div className="space-y-2">
                    {r.reserve_projects.map((p, i) => (
                      <div key={`proj-${p.idea}-${i}`} className="p-3 rounded-lg bg-accent/30 border border-border flex items-center justify-between" data-testid={`proj-${i}`}>
                        <div>
                          <div className="no-gold text-sm font-semibold">{p.idea}</div>
                          <div className="text-xs text-muted-foreground">source: {p.source}</div>
                        </div>
                        <div className="text-gold font-mono text-sm">{p.est_savings}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {r.cpa_ready_notes?.length > 0 && (
                <div className="hoa-card p-6" data-testid="cpa-notes">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gold-soft mb-3"><FileCheck2 className="w-3.5 h-3.5" /> CPA-ready notes</div>
                  <ul className="space-y-1.5">
                    {r.cpa_ready_notes.map((c, i) => (
                      <li key={`cpa-${i}-${String(c).slice(0, 24)}`} className="no-gold text-sm text-foreground/85">✓ {c}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {auditData && !selected && (
            <div className="hoa-card p-6 print:bg-white print:text-black" data-testid="audit-packet">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-xl font-semibold">CPA Audit-Ready Packet</h2>
                <Button variant="outline" size="sm" onClick={() => window.print()}>Print</Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {Object.entries(auditData.snapshot).map(([k, v]) => (
                  <div key={k} className="p-3 rounded-lg bg-accent/30 border border-border">
                    <div className="text-[10px] uppercase text-muted-foreground">{k.replace(/_/g, " ")}</div>
                    <div className="font-mono text-gold">{typeof v === "number" ? v.toLocaleString() : v}</div>
                  </div>
                ))}
              </div>
              <h3 className="text-lg font-semibold mb-2">Reconciliation notes</h3>
              <ul className="space-y-1.5 mb-4">
                {auditData.reconciliation_notes.map((n, i) => (
                  <li key={`recon-${i}-${String(n).slice(0, 24)}`} className="no-gold text-sm text-foreground/85">• {n}</li>
                ))}
              </ul>
              <h3 className="text-lg font-semibold mb-2">Ledger ({auditData.ledger_entries.length} entries)</h3>
              <div className="max-h-80 overflow-y-auto text-xs font-mono border border-border rounded-lg p-3 bg-accent/20">
                {auditData.ledger_entries.map((e) => (
                  <div key={e.id} className="flex justify-between py-1 border-b border-border/50">
                    <span className="no-gold">{e.created_at.slice(0, 10)} · {e.owner_name} · {e.description || e.kind}</span>
                    <span className={e.kind === "charge" ? "text-destructive" : "text-gold"}>{e.kind === "charge" ? "+" : "-"}${e.amount.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
